import React, { useCallback } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { IconDashboard, IconClips, IconCalendar, IconInsights, IconAdmin, IconSignout } from './Icons';

const palettes = [
  // 0 — Clump green (default)
  { bg:'#0a0a0a', surface:'#111111', surface2:'#1a1a1a', border:'#222222', accent:'#c8f135', accentDim:'rgba(200,241,53,0.12)', text:'#f0f0f0', muted:'#666666', dim:'#999999' },
  // 1 — Midnight blue
  { bg:'#06090f', surface:'#0c1120', surface2:'#111829', border:'#1a2338', accent:'#5b9cf6', accentDim:'rgba(91,156,246,0.12)', text:'#e8eef8', muted:'#4a5568', dim:'#718096' },
  // 2 — Deep plum + rose gold
  { bg:'#0a0610', surface:'#120c1c', surface2:'#1a1226', border:'#261830', accent:'#e8a0b4', accentDim:'rgba(232,160,180,0.12)', text:'#f0eaf5', muted:'#6b5070', dim:'#9a7a90' },
  // 3 — Forest + mint
  { bg:'#060a07', surface:'#0d140e', surface2:'#121c13', border:'#1a2a1b', accent:'#7dd4b0', accentDim:'rgba(125,212,176,0.12)', text:'#e5f0ea', muted:'#3d5e48', dim:'#6a9078' },
  // 4 — Charcoal + lavender
  { bg:'#0c0b10', surface:'#141220', surface2:'#1c192c', border:'#262238', accent:'#b09ee0', accentDim:'rgba(176,158,224,0.12)', text:'#edeaf8', muted:'#5a5070', dim:'#8a80b0' },
  // 5 — Blush + burgundy
  { bg:'#fdf5f5', surface:'#f7ecec', surface2:'#f0e0e0', border:'#e2cccc', accent:'#7b2d3e', accentDim:'rgba(123,45,62,0.07)', text:'#1a0f12', muted:'#8a6670', dim:'#6b4f58' },
  // 6 — Ivory + cobalt
  { bg:'#f8f7f4', surface:'#f1efe9', surface2:'#e8e5dc', border:'#d8d3c8', accent:'#2b4fa8', accentDim:'rgba(43,79,168,0.07)', text:'#0f1220', muted:'#6070a0', dim:'#485580' },
  // 7 — Pale lavender + plum
  { bg:'#f8f5fc', surface:'#f0eaf8', surface2:'#e6dff0', border:'#d4cce5', accent:'#5c2d7a', accentDim:'rgba(92,45,122,0.07)', text:'#14101a', muted:'#786890', dim:'#5a5070' },
  // 8 — Cream + crimson
  { bg:'#fdfaf4', surface:'#f6f0e4', surface2:'#ede4d4', border:'#ddd4be', accent:'#8b1a1a', accentDim:'rgba(139,26,26,0.07)', text:'#1a1208', muted:'#8a7055', dim:'#6b5438' },
  // 9 — Ink + amber
  { bg:'#0a0900', surface:'#120f00', surface2:'#1a1500', border:'#28200a', accent:'#d4a843', accentDim:'rgba(212,168,67,0.12)', text:'#f0e8d0', muted:'#6b5830', dim:'#9a8550' },
  // 10 — Pale cornflower + ruby
  { bg:'#eaecf8', surface:'#e0e3f4', surface2:'#d6daef', border:'#c4c9e8', accent:'#c0392b', accentDim:'rgba(192,57,43,0.08)', text:'#0f0f1a', muted:'#7070a0', dim:'#505088' },
];

let currentPaletteIdx = (() => {
  try { return parseInt(localStorage.getItem('clump_palette_index') || '0') || 0; } catch { return 0; }
})();

function hexToRgba(hex, a) {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
}

// Apply saved palette on load
if (typeof window !== 'undefined') {
  applyPalette(palettes[currentPaletteIdx]);
}

function applyPalette(p) {
  const s = document.documentElement.style;
  s.setProperty('--bg', p.bg);
  s.setProperty('--surface', p.surface);
  s.setProperty('--surface2', p.surface2);
  s.setProperty('--border', p.border);
  s.setProperty('--accent', p.accent);
  s.setProperty('--accent-dim', p.accentDim);
  s.setProperty('--text', p.text);
  s.setProperty('--text-muted', p.muted);
  s.setProperty('--text-dim', p.dim);
  // Also update green/red which are static — keep them but adjust red for light themes
  const isLight = parseInt(p.bg.slice(1,3),16) > 128;
  s.setProperty('--green', p.accent);
  if (isLight) {
    s.setProperty('--red', '#cc2222');
  } else {
    s.setProperty('--red', '#ff4444');
  }
}

const IconAccount = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);

const IconShuffle = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/>
    <polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/>
  </svg>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate('/login'); };

  const randomise = useCallback(() => {
    let next;
    do { next = Math.floor(Math.random() * palettes.length); } while (next === currentPaletteIdx);
    currentPaletteIdx = next;
    try { localStorage.setItem('clump_palette_index', next); } catch {}
    document.body.classList.add('theme-transitioning');
    applyPalette(palettes[next]);
    setTimeout(() => document.body.classList.remove('theme-transitioning'), 500);
  }, []);

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="sidebar-logo">Clump</div>
        <nav className="sidebar-nav">
          <NavLink to="/dashboard" className={({isActive}) => `nav-item${isActive?' active':''}`}>
            <IconDashboard color={undefined} /> Dashboard
          </NavLink>
          <NavLink to="/clips" className={({isActive}) => `nav-item${isActive?' active':''}`}>
            <IconClips color={undefined} /> Clips
          </NavLink>
          <NavLink to="/calendar" className={({isActive}) => `nav-item${isActive?' active':''}`}>
            <IconCalendar color={undefined} /> Calendar
          </NavLink>
          <NavLink to="/insights" className={({isActive}) => `nav-item${isActive?' active':''}`}>
            <IconInsights color={undefined} /> Insights
          </NavLink>
{user?.role === 'admin' && (
            <NavLink to="/admin" className={({isActive}) => `nav-item${isActive?' active':''}`}>
              <IconAdmin color={undefined} /> Admin
            </NavLink>
          )}
        </nav>
        <div className="sidebar-footer">
          <div className="sidebar-user" style={{marginBottom:8}}>{user?.display_name || user?.email}</div>
          <button
            onClick={randomise}
            title="Randomise theme"
            style={{
              width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:6,
              padding:'8px', marginBottom:6, fontSize:12, fontWeight:500,
              background:'transparent', border:'1px solid var(--border)', borderRadius:6,
              color:'var(--text-muted)', cursor:'pointer', fontFamily:'var(--font-body)',
              transition:'all 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor='var(--accent)'; e.currentTarget.style.color='var(--accent)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.color='var(--text-muted)'; }}
          >
            <IconShuffle /> Randomise
          </button>
          <NavLink
            to="/account"
            style={({isActive}) => ({
              width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:6,
              padding:'8px', marginBottom:6, fontSize:12, fontWeight:500,
              background:'transparent', border:`1px solid ${isActive ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius:6, color: isActive ? 'var(--accent)' : 'var(--text-muted)',
              cursor:'pointer', fontFamily:'var(--font-body)', textDecoration:'none',
              transition:'all 0.2s',
            })}
          >
            <IconAccount /> Account
          </NavLink>
          <button className="btn btn-ghost" style={{width:'100%', fontSize:'13px', padding:'8px', display:'flex', alignItems:'center', justifyContent:'center', gap:6}} onClick={handleLogout}>
            <IconSignout /> Sign out
          </button>
        </div>
      </aside>
      <main className="main"><Outlet /></main>
    </div>
  );
}
